import React, { useState } from 'react';
import { HashLink } from 'react-router-hash-link';
import { 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  User,
  CheckCircle,
  Search
} from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    serviceType: []
  });
  const [isBookingSubmitted, setIsBookingSubmitted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const openBookingModal = () => {
    setIsBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setIsBookingSubmitted(false);
    setBookingData({
      name: '',
      phone: '',
      email: '',
      date: '',
      time: '',
      serviceType: []
    });
    setSearchQuery('');
  };

  const handleBookingChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      setBookingData(prevData => ({
        ...prevData,
        serviceType: checked 
          ? [...prevData.serviceType, value]
          : prevData.serviceType.filter(service => service !== value)
      }));
    } else {
      setBookingData({ ...bookingData, [name]: value });
    }
  };
  
  const handleBookingSubmit = (e) => {
    e.preventDefault();
    console.log("Booking submitted:", bookingData);
    setIsBookingSubmitted(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRemoveService = (serviceToRemove) => {
    setBookingData(prevData => ({
      ...prevData,
      serviceType: prevData.serviceType.filter(service => service !== serviceToRemove)
    }));
  };

  const services = [
    "تحليل صورة الدم الكاملة (CBC)",
    "تحليل وظائف الكبد (LFTs)",
    "تحليل وظائف الكلى (KFTs)",
    "تحليل السكر التراكمي (HbA1c)",
    "تحليل الغدة الدرقية (TSH, T3, T4)",
    "تحليل الفيتامينات والمعادن",
    "تحليل الدهون الكاملة (Lipid Panel)",
    "تحليل البول الكامل (Urine Analysis)",
    "تحليل الحمل (Pregnancy Test)",
    "تحليل الأنسولين (Insulin Test)",
    "تحليل الكوليسترول (LDL, HDL)",
    "تحليل المخدرات (Drug Screening)",
    "تحليل الهرمونات",
    "تحليل الفيروسات",
    "تحليل inbody",    
    "تحليل السائل المنوي",
    "تحليل الدم الخفي في البراز",
    "تحليل PSA للبروستاتا",
    "تحليل دلالات الأورام",
    "تحليل فيتامين د",
    "تحليل نسبة الحديد والفيريتين",
    "تحليل وظائف القلب (Troponin)",
    "تحليل PCR للفيروسات",
    "تحليل الحساسية"
  ];

  const filteredServices = services.filter(service =>
    service.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <header className="bg-white shadow-sm font-sans text-right sticky top-0 z-40">
        {/* شريط علوي */}
        <div className="bg-gradient-to-l from-blue-900 to-blue-700 text-white py-2">
          <div className="container mx-auto px-4 flex justify-between items-center text-sm">
            <div className="flex items-center gap-5">
              <a href="tel:0123456790" className="flex items-center gap-2 hover:text-teal-300 transition-colors">
                <Phone className="w-4 h-4" />
                <span>012-3456-7890</span>
              </a>
              <a href="mailto:Elite@medicallab.com" className="hidden sm:flex items-center gap-2 hover:text-teal-300 transition-colors">
                <Mail className="w-4 h-4" />
                <span>Elite@medicallab.com</span>
              </a>
            </div>
            <a href="#visit" className="flex items-center gap-2 hover:text-teal-300 transition-colors">
              <MapPin className="w-4 h-4" />
              <span>فروعنا</span>
            </a>
          </div>
        </div>

        {/* شريط التنقل الرئيسي */}
        <nav className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* الشعار */}
            <a href="#home" className="flex items-center group">
              <div className="bg-gradient-to-br from-blue-700 to-teal-600 text-white p-2 rounded-xl ml-3 shadow-md group-hover:shadow-lg transition-shadow">
                <div className="w-8 h-8 flex items-center justify-center font-bold text-lg">
                  النخبه
                </div>
              </div>
              <div>
                <h1 className="text-xl font-extrabold text-blue-900 tracking-tight">معمل النخبه</h1>
                <p className="text-sm text-gray-500">تحاليل طبية دقيقة وموثوقة</p>
              </div>
            </a>

            {/* التنقل لسطح المكتب */}
            <div className="hidden md:flex items-center gap-7">
              <HashLink smooth to="/#home" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                الرئيسية
              </HashLink>

              <HashLink smooth to="/#services" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                خدماتنا
              </HashLink>

              <HashLink smooth to="/#packages" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                الباقات المتخصصة
              </HashLink>

              <HashLink smooth to="/#tests" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                التحاليل الفردية
              </HashLink>

              <HashLink smooth to="/#about" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                من نحن
              </HashLink>

              <HashLink smooth to="/#contact" className="text-gray-600 font-medium hover:text-teal-600 transition-colors border-b-2 border-transparent hover:border-teal-500 pb-1">
                تواصل معنا
              </HashLink>

              <button
                onClick={openBookingModal}
                className="bg-gradient-to-l from-blue-700 to-teal-600 hover:shadow-lg hover:-translate-y-0.5 text-white font-semibold py-2.5 px-6 rounded-full shadow-md transition-all duration-300"
              >
                احجز موعدك
              </button>
            </div>

            {/* زر قائمة الجوال */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* قائمة الجوال - تظهر فقط عند فتحها (تم إصلاح الخطأ هنا) */}
          {isMenuOpen && (
            <div className="flex flex-col space-y-4 pt-4 mt-4 border-t border-gray-200 md:hidden">
              <HashLink smooth to="/#home" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                الرئيسية
              </HashLink>

              <HashLink smooth to="/#services" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                خدماتنا
              </HashLink>

              <HashLink smooth to="/#packages" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                الباقات المتخصصة
              </HashLink>

              <HashLink smooth to="/#tests" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                التحاليل الفردية
              </HashLink>

              <HashLink smooth to="/#about" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                من نحن
              </HashLink>

              <HashLink smooth to="/#contact" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-teal-600 transition-colors">
                تواصل معنا
              </HashLink>

              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  openBookingModal();
                }}
                className="bg-gradient-to-l from-blue-700 to-teal-600 text-white font-semibold py-2 px-6 rounded-full shadow-lg transition-colors duration-300 w-full"
              >
                احجز موعدك
              </button>
            </div>
          )}
        </nav>
      </header>

      {/* نافذة حجز الموعد (Modal) */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 bg-blue-950/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans text-right">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-auto relative transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto">
            {/* زر الإغلاق */}
            <button
              onClick={closeBookingModal}
              className="absolute top-8 left-6 text-gray-700 hover:text-gray-900 cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>
            
            {!isBookingSubmitted ? (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-blue-900">
                    احجز موعدك الآن
                  </h3>
                  <p className="text-gray-600 mt-2">
                    املأ البيانات وسنتواصل معك لتأكيد الموعد.
                  </p>
                </div>
                
                <form onSubmit={handleBookingSubmit} className="space-y-4">
                  {/* الاسم */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      الاسم الكامل
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={bookingData.name}
                        onChange={handleBookingChange}
                        required
                        className="block w-full pr-10 pl-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* البريد الإلكتروني */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      البريد الإلكتروني
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={bookingData.email}
                        onChange={handleBookingChange}
                        required
                        className="block w-full pr-10 pl-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* رقم الهاتف */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      رقم الهاتف
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={bookingData.phone}
                        onChange={handleBookingChange}
                        required
                        className="block w-full pr-10 pl-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* التاريخ والوقت */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full sm:w-1/2">
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                        التاريخ
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <input
                          type="date"
                          id="date"
                          name="date"
                          value={bookingData.date}
                          onChange={handleBookingChange}
                          required
                          className="block w-full px-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                        />
                      </div>
                    </div>
                    <div className="w-full sm:w-1/2">
                      <label className="block text-sm font-medium text-gray-700">
                        وقت الزيارة
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <input
                          type="time"
                          name="time"
                          value={bookingData.time}
                          onChange={handleBookingChange}
                          className="w-full px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* نوع الخدمة (مربعات اختيار مع حقل بحث) */}
                  <div>
                    <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                      التحاليل المطلوبة (يمكنك اختيار أكثر من تحليل)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm mb-4">
                      <input
                        type="text"
                        placeholder="ابحث عن تحليل..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="block w-full pr-10 pl-4 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-right"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-right">
                      {filteredServices.length > 0 ? (
                        filteredServices.map((service, index) => (
                          <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse cursor-pointer">
                            <input
                              type="checkbox"
                              id={`service-${index}`}
                              name="serviceType"
                              value={service}
                              checked={bookingData.serviceType.includes(service)}
                              onChange={handleBookingChange}
                              className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                            />
                            <label htmlFor={`service-${index}`} className="text-sm text-gray-700 cursor-pointer">
                              {service}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm text-center col-span-full">لا توجد نتائج مطابقة.</p>
                      )}
                    </div>
                  </div>

                  {/* قسم عرض التحاليل المختارة */}
                  {bookingData.serviceType.length > 0 && (
                    <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                      <h4 className="text-lg font-bold text-teal-700 mb-2">التحاليل المحددة:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-right">
                        {bookingData.serviceType.map((service, index) => (
                          <div
                            key={index}
                            className="bg-teal-100 text-teal-800 text-sm font-medium px-3 py-1 rounded-full border border-teal-300 flex items-center gap-1 rtl:flex-row-reverse justify-between"
                          >
                            <span>{service}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveService(service)}
                              className="text-teal-500 hover:text-teal-700 p-1 rounded-full hover:bg-teal-200 transition-colors cursor-pointer"
                              aria-label={`إزالة ${service}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* أزرار الإرسال والإغلاق */}
                  <div className="flex justify-end space-x-4 rtl:space-x-reverse mt-6">
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-3 px-8 rounded-full hover:shadow-lg transition duration-300 shadow-md cursor-pointer"
                    >
                      تأكيد الحجز
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center p-8">
                <CheckCircle className="w-16 h-16 mx-auto text-teal-500 mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  تم حجز موعدك بنجاح!
                </h3>
                <p className="text-gray-600">
                  سنتواصل معك قريباً لتأكيد تفاصيل الموعد.
                </p>
                <button
                  onClick={closeBookingModal}
                  className="mt-6 bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-2 px-6 rounded-full hover:shadow-lg transition duration-300 cursor-pointer"
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
